import { useEffect, useState } from 'react'
import {
  useEditRequestLocationGroupMutation,
  useShippingLocationGroupsQuery,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import ModalForm from '@/gf/components/ModalForm'
import Spinner from '@/gf/components/Spinner'
import useMsgs from '@/gf/hooks/useMsgs'

const EditRequestLocationGroupModal = ({
  open,
  onClose,
  currentLocationGroupId,
  requestId,
  refetch,
}: {
  open: boolean
  onClose: () => void
  currentLocationGroupId: string | null | undefined
  requestId: string
  refetch: () => unknown
}) => {
  const [_msgs, messages] = useMsgs()
  const { orgId } = useSession()
  const client = useGqlClient()
  const [locationGroupId, setLocationGroupId] = useState(currentLocationGroupId || '')
  const [editRequestLocationGroup] = useEditRequestLocationGroupMutation({ client })

  // Sync state when modal opens or currentLocationGroupId changes
  useEffect(() => {
    if (open) {
      setLocationGroupId(currentLocationGroupId || '')
    }
  }, [open, currentLocationGroupId])

  const { data, loading, error } = useShippingLocationGroupsQuery({
    client,
    variables: { orgId, filter: null },
    skip: !orgId,
  })

  const locationGroups = data?.org?.shippingLocationGroups ?? []

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    editRequestLocationGroup({
      variables: { id: requestId, shippingLocationGroupId: locationGroupId || null },
    })
      .then(() => {
        refetch()
        messages.add('Location Group Updated!', 'positive')
      })
      .catch(() => messages.add('Error updating location group', 'negative'))
      .finally(onClose)
  }

  return (
    <ModalForm
      title="Edit Location Group"
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      submitButtonText="Save"
    >
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Spinner />
        </div>
      ) : error ? (
        <div className="text-red-600 text-sm">
          Failed to load location groups. Please try again.
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Location Group</label>
          <select
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={locationGroupId}
            onChange={(e) => setLocationGroupId(e.target.value)}
          >
            <option value="">No location group</option>
            {locationGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </ModalForm>
  )
}

export default EditRequestLocationGroupModal
